/**
 * Structural diff between two saved project states (migration 006 UI).
 *
 * Platform-neutral by construction: no browser globals, no node:* imports, no
 * builder-core imports. Runs identically under jsdom, Node, and workerd —
 * version-diff-tests.js exercises it in plain Node.
 *
 * Inputs are two ALREADY-CANONICALIZED states (read through
 * core/save-load.js readStateFromZip, or the live state through
 * preparePortableState) plus an injected categoryMap
 * (data/category-map.json — R2-served, so this module never fetches it; a
 * missing map degrades labels to raw category keys). Inputs are never mutated.
 *
 * THE PARITY INVARIANT: `diffVersionStates(a, b).identical` must coincide with
 * `versionContentHashOf(a) === versionContentHashOf(b)` (shared/version-hash.js).
 * Both therefore build their comparison text the same way: canonicalStringify
 * over the state minus VERSION_HASH_IGNORED_PATHS — this module imports that
 * constant rather than keeping its own list, and version-diff-tests.js asserts
 * the parity on a fixture battery. Never widen one side's ignore set alone.
 *
 * KNOWN RESIDUAL: structural migration rewrites (decorative-svg, lamp,
 * container-scroll root migrations in core/save-load.js migrateState) change
 * section markup beyond the volatile attributes below, so a bundle saved
 * before such a migration diffs as 'edited' against one saved after, even when
 * the user changed nothing. Rare (only across schema-migration boundaries) and
 * accepted — the volatile-attr normalization must NOT grow to swallow it,
 * because the same breadth would swallow real attribute edits.
 */

import { canonicalStringify } from './collab-protocol.js';
import { VERSION_HASH_IGNORED_PATHS } from './version-hash.js';
import { acceptKeysForIdentity } from './audit-accept-schema.js';
import { diffLines } from './line-diff.js';
import { SHARED_SECTION_CATEGORIES, sharedSectionLabel } from './shared-section-types.js';

// ── display names ───────────────────────────────────────────

/**
 * The section display-name ladder, extracted from its two previously-duplicated
 * call sites (ui/section-list.js `_resolveRowContext`, sections/section-manager.js
 * `getSectionDisplayLabel`) — both now import this.
 *
 * @param {object} section
 * @param {object} [categoryMap] — data/category-map.json shape:
 *   {[category]: {displayName, ...}}
 */
// PP-1: the ladder itself now lives in shared/section-categories.js beside the
// category order and the insert-position sentence; re-exported here so every
// existing importer keeps working unchanged.
import { sectionDisplayName } from './section-categories.js';
export { sectionDisplayName };

// ── html normalization ──────────────────────────────────────

/**
 * Editor-stamped attributes that appear/disappear WITHOUT a user edit — an
 * explicit allowlist, deliberately NOT a data-pb-* wildcard:
 *  - data-pb-eid: stamped into section html by every load path
 *    (editing/editable-utils.js stampEditableIds)
 *  - data-pb-motion-inert: stamped by the motion-residue normalizer
 *    (shared/motion-residue.js)
 * Semantic data-pb-* attrs — data-pb-asset (durable asset identity),
 * data-pb-client / data-pb-client-text (business-info bindings) — are REAL
 * content: a change there is a user edit and must never be normalized away.
 */
export const VOLATILE_HTML_ATTRS = Object.freeze(['data-pb-eid', 'data-pb-motion-inert']);

const VOLATILE_ATTR_RE = new RegExp(
  `\\s(?:${VOLATILE_HTML_ATTRS.join('|')})(?:\\s*=\\s*"[^"]*"|\\s*=\\s*'[^']*')?(?=[\\s/>])`,
  'g'
);

/** Strip ONLY the volatile editor stamps. Pure string ops — no DOM. */
export function normalizeSectionHtml(html) {
  if (typeof html !== 'string' || !html) return '';
  return html.replace(VOLATILE_ATTR_RE, '');
}

// ── section comparison ──────────────────────────────────────

const nonNullString = (value) => (typeof value === 'string' ? value : '');

/**
 * Field-level comparison of one matched section pair.
 * @returns {{changed: boolean, modernizedOnly: boolean, fields: string[]}}
 */
function compareSectionPair(oldSection, newSection) {
  const fields = [];
  let modernizedOnly = false;

  const oldHtml = nonNullString(oldSection.html);
  const newHtml = nonNullString(newSection.html);
  if (oldHtml !== newHtml) {
    if (normalizeSectionHtml(oldHtml) === normalizeSectionHtml(newHtml)) {
      // Volatile-stamp churn only — not a user edit.
      modernizedOnly = true;
    } else {
      fields.push('html');
    }
  }

  if (
    canonicalStringify(oldSection.editableIdRepairs) !==
    canonicalStringify(newSection.editableIdRepairs)
  )
    fields.push('editableIdRepairs');

  if (nonNullString(oldSection.css) !== nonNullString(newSection.css)) fields.push('css');
  if (nonNullString(oldSection.js) !== nonNullString(newSection.js)) fields.push('js');
  if (nonNullString(oldSection.displayName) !== nonNullString(newSection.displayName)) {
    fields.push('displayName');
  }
  if (
    canonicalStringify(oldSection.overrides ?? null) !==
    canonicalStringify(newSection.overrides ?? null)
  ) {
    fields.push('overrides');
  }

  return {
    changed: fields.length > 0,
    modernizedOnly: modernizedOnly && fields.length === 0,
    fields
  };
}

/**
 * Match two section lists.
 * Primary key: instanceId (stable across save/load — loadFromZip never
 * regenerates it). Fallback for pre-instanceId states: pair leftovers by
 * (catalogKey, occurrence index) in order — without it, ancient histories read
 * as total rewrites.
 * @returns {{pairs: Array<{oldSection, newSection}>, added: object[], removed: object[]}}
 */
function matchSections(oldSections, newSections) {
  const oldList = Array.isArray(oldSections) ? oldSections : [];
  const newList = Array.isArray(newSections) ? newSections : [];

  const oldById = new Map();
  for (const section of oldList) {
    if (section?.instanceId) oldById.set(section.instanceId, section);
  }

  const pairs = [];
  const added = [];
  const matchedOld = new Set();

  const unpairedNew = [];
  for (const section of newList) {
    const match = section?.instanceId ? oldById.get(section.instanceId) : null;
    if (match && !matchedOld.has(match)) {
      matchedOld.add(match);
      pairs.push({ oldSection: match, newSection: section });
    } else {
      unpairedNew.push(section);
    }
  }

  // Occurrence-index fallback per catalogKey.
  const leftoversOldByKey = new Map();
  for (const section of oldList) {
    if (matchedOld.has(section)) continue;
    const key = section?.catalogKey || section?.sectionId || '';
    if (!leftoversOldByKey.has(key)) leftoversOldByKey.set(key, []);
    leftoversOldByKey.get(key).push(section);
  }
  for (const section of unpairedNew) {
    const key = section?.catalogKey || section?.sectionId || '';
    const bucket = leftoversOldByKey.get(key);
    const match = bucket?.length ? bucket.shift() : null;
    if (match) {
      matchedOld.add(match);
      pairs.push({ oldSection: match, newSection: section });
    } else {
      added.push(section);
    }
  }

  const removed = oldList.filter((section) => !matchedOld.has(section));
  return { pairs, added, removed };
}

/** Pair identity for order comparison (works for fallback-matched pairs too). */
function pairKey(pair, index) {
  return pair.newSection?.instanceId || pair.oldSection?.instanceId || `pair-${index}`;
}

/**
 * Section-level diff for one matched page.
 * Moved = off the LCS of the SURVIVING pair sequence (diffLines reused as the
 * LCS engine) — a section whose index shifted because neighbors were
 * added/removed is NOT moved; an edited section reports 'edited' even when it
 * also moved (edit wins).
 */
function diffSectionsOfPage(oldSections, newSections, categoryMap) {
  const { pairs, added, removed } = matchSections(oldSections, newSections);

  const keys = pairs.map(pairKey);
  const byOldOrder = [...pairs].sort(
    (a, b) =>
      (Array.isArray(oldSections) ? oldSections.indexOf(a.oldSection) : 0) -
      (Array.isArray(oldSections) ? oldSections.indexOf(b.oldSection) : 0)
  );
  const oldKeySeq = byOldOrder.map((pair) => keys[pairs.indexOf(pair)]).join('\n');
  const newKeySeq = keys.join('\n');
  const movedKeys = new Set();
  if (oldKeySeq !== newKeySeq) {
    // Hunk lines are prefixed strings ('-key' / '+key' / ' key'); off-LCS keys
    // appear with a +/- prefix and those are the moved ones.
    const { hunks } = diffLines(oldKeySeq, newKeySeq, { context: 0, maxLines: 10_000 });
    for (const hunk of hunks) {
      for (const line of hunk.lines) {
        const kind = line[0];
        if (kind === '+' || kind === '-') movedKeys.add(line.slice(1));
      }
    }
  }

  const sections = [];
  for (let i = 0; i < pairs.length; i += 1) {
    const { oldSection, newSection } = pairs[i];
    const { changed, modernizedOnly, fields } = compareSectionPair(oldSection, newSection);
    const label = sectionDisplayName(newSection, categoryMap);
    const instanceId = newSection?.instanceId || oldSection?.instanceId || null;
    if (changed) {
      sections.push({ kind: 'edited', instanceId, label, fields });
    } else if (modernizedOnly) {
      sections.push({ kind: 'modernized', instanceId, label, fields: [] });
    } else if (movedKeys.has(keys[i])) {
      sections.push({ kind: 'moved', instanceId, label, fields: [] });
    }
  }
  for (const section of added) {
    sections.push({
      kind: 'added',
      instanceId: section?.instanceId || null,
      label: sectionDisplayName(section, categoryMap),
      fields: []
    });
  }
  for (const section of removed) {
    sections.push({
      kind: 'removed',
      instanceId: section?.instanceId || null,
      label: sectionDisplayName(section, categoryMap),
      fields: []
    });
  }

  const reordered = movedKeys.size > 0;
  return { sections, reordered };
}

// ── page matching ───────────────────────────────────────────

/**
 * Match pages by id, then by name — but a name only pairs when it is UNIQUE on
 * BOTH sides (two "Landing" pages must not mispair; they fall through to
 * add/remove). The name fallback exists because migrateState regenerates
 * missing/duplicate page ids, so id churn alone must not read as remove+add.
 */
function matchPages(oldPages, newPages) {
  const oldList = Array.isArray(oldPages) ? oldPages : [];
  const newList = Array.isArray(newPages) ? newPages : [];

  const oldById = new Map(oldList.filter((p) => p?.id).map((p) => [p.id, p]));
  const matchedOld = new Set();
  const pairs = new Map(); // newPage → oldPage

  for (const page of newList) {
    const match = page?.id ? oldById.get(page.id) : null;
    if (match && !matchedOld.has(match)) {
      matchedOld.add(match);
      pairs.set(page, match);
    }
  }

  const nameCounts = (list) => {
    const counts = new Map();
    for (const page of list) {
      const name = String(page?.name || '');
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return counts;
  };
  const oldNameCounts = nameCounts(oldList);
  const newNameCounts = nameCounts(newList);
  const oldByName = new Map();
  for (const page of oldList) {
    if (!matchedOld.has(page) && oldNameCounts.get(String(page?.name || '')) === 1) {
      oldByName.set(String(page?.name || ''), page);
    }
  }
  for (const page of newList) {
    if (pairs.has(page)) continue;
    const name = String(page?.name || '');
    if (newNameCounts.get(name) !== 1) continue;
    const match = oldByName.get(name);
    if (match && !matchedOld.has(match)) {
      matchedOld.add(match);
      pairs.set(page, match);
    }
  }

  const removed = oldList.filter((page) => !matchedOld.has(page));
  return { pairs, removed };
}

const pageHasPendingRefs = (page) =>
  Array.isArray(page?._pendingTemplateRefs) && page._pendingTemplateRefs.length > 0;

// ── settings buckets ────────────────────────────────────────

/**
 * Top-level design/settings comparisons. Objects via canonicalStringify,
 * scalars via ===. One human label per bucket — the summary vocabulary.
 */
const SETTINGS_BUCKETS = [
  { bucket: 'theme', label: 'Colors changed', paths: ['theme'], object: true },
  {
    bucket: 'fonts',
    label: 'Fonts changed',
    paths: ['font', 'headingFont', 'headingFontWeight'],
    object: false
  },
  { bucket: 'darkMode', label: 'Dark mode setting changed', paths: ['darkMode'], object: false },
  { bucket: 'buttons', label: 'Button styles changed', paths: ['buttonStyles'], object: true },
  { bucket: 'forms', label: 'Form styles changed', paths: ['formStyles'], object: true },
  { bucket: 'effects', label: 'Site effects changed', paths: ['siteEffects'], object: true },
  {
    bucket: 'entrance',
    label: 'Entrance animations changed',
    paths: ['entrance'],
    object: true
  },
  {
    bucket: 'icons',
    label: 'Icon style changed',
    paths: ['iconTinting', 'fontHotlinkFallback'],
    object: false
  },
  {
    bucket: 'customCode',
    label: 'Custom code changed',
    paths: ['headCode', 'bodyCode'],
    object: false
  },
  { bucket: 'pwa', label: 'App install settings changed', paths: ['pwaInstall'], object: true },
  {
    bucket: 'features',
    label: 'Site features changed',
    paths: ['features', 'flags'],
    object: true
  },
  { bucket: 'brandVoice', label: 'Brand voice changed', paths: ['brandVoice'], object: true },
  { bucket: 'clientInfo', label: 'Business info changed', paths: ['clientInfo'], object: true },
  { bucket: 'deploy', label: 'Deploy settings changed', paths: ['deploy'], object: true },
  { bucket: 'projectName', label: 'Project renamed', paths: ['projectName'], object: false }
];

function diffSettings(oldState, newState) {
  const changes = [];
  for (const { bucket, label, paths, object } of SETTINGS_BUCKETS) {
    const differs = paths.some((path) =>
      object
        ? canonicalStringify(oldState?.[path] ?? null) !==
          canonicalStringify(newState?.[path] ?? null)
        : (oldState?.[path] ?? null) !== (newState?.[path] ?? null)
    );
    if (differs) changes.push({ bucket, label });
  }
  return changes;
}

// ── the engine ──────────────────────────────────────────────

function stripIgnored(state) {
  const clone = structuredClone(state);
  for (const path of VERSION_HASH_IGNORED_PATHS) {
    if (clone != null && typeof clone === 'object') delete clone[path];
  }
  if (clone && typeof clone === 'object') {
    const acceptKeys = acceptKeysForIdentity(clone.auditAccepts);
    if (acceptKeys.length > 0) clone.auditAccepts = acceptKeys;
    else delete clone.auditAccepts;
  }
  return clone;
}

/**
 * @param {object} oldState — canonicalized (readStateFromZip / preparePortableState)
 * @param {object} newState — same
 * @param {{categoryMap?: object}} [opts]
 * @returns {VersionDiff}
 */
export function diffVersionStates(oldState, newState, { categoryMap = {} } = {}) {
  // Fast path AND parity anchor: this is byte-for-byte the text
  // versionContentHashOf digests, so hash-equal ⇔ identical by construction.
  const identical =
    canonicalStringify(stripIgnored(oldState)) === canonicalStringify(stripIgnored(newState));

  const empty = {
    identical,
    modernizedOnly: false,
    internalOnly: false,
    pagesReordered: false,
    pages: [],
    shared: { navigation: null, footer: null },
    settings: []
  };
  if (identical) return empty;

  const oldPages = Array.isArray(oldState?.pages) ? oldState.pages : [];
  const newPages = Array.isArray(newState?.pages) ? newState.pages : [];
  const { pairs, removed } = matchPages(oldPages, newPages);

  const pages = [];
  for (const page of newPages) {
    const oldPage = pairs.get(page);
    if (!oldPage) {
      pages.push({
        kind: 'added',
        pageId: page?.id || null,
        name: String(page?.name || 'Untitled'),
        oldName: null,
        renamed: false,
        seoChanged: false,
        reordered: false,
        // A pending page's sections aren't hydrated — a count would lie.
        sections: [],
        pending: pageHasPendingRefs(page)
      });
      continue;
    }

    const renamed = String(oldPage?.name || '') !== String(page?.name || '');
    const seoChanged =
      canonicalStringify(oldPage?.seo ?? null) !== canonicalStringify(page?.seo ?? null);

    if (pageHasPendingRefs(oldPage) || pageHasPendingRefs(page)) {
      // Either side unhydrated → section lists are not comparable. Never report
      // "N sections removed" for a page that simply hasn't loaded.
      pages.push({
        kind: 'not-loaded',
        pageId: page?.id || null,
        name: String(page?.name || 'Untitled'),
        oldName: renamed ? String(oldPage?.name || '') : null,
        renamed,
        seoChanged,
        reordered: false,
        sections: []
      });
      continue;
    }

    const { sections, reordered } = diffSectionsOfPage(
      oldPage?.sections,
      page?.sections,
      categoryMap
    );
    const meaningful = sections.filter((s) => s.kind !== 'modernized');
    if (meaningful.length || renamed || seoChanged || reordered) {
      pages.push({
        kind: meaningful.length || seoChanged || reordered ? 'edited' : 'renamed',
        pageId: page?.id || null,
        name: String(page?.name || 'Untitled'),
        oldName: renamed ? String(oldPage?.name || '') : null,
        renamed,
        seoChanged,
        reordered,
        sections
      });
    } else if (sections.length) {
      // Only modernized churn — keep it visible to modernizedOnly detection
      // below but out of the summary path.
      pages.push({
        kind: 'unchanged',
        pageId: page?.id || null,
        name: String(page?.name || 'Untitled'),
        oldName: null,
        renamed: false,
        seoChanged: false,
        reordered: false,
        sections
      });
    }
  }
  for (const page of removed) {
    pages.push({
      kind: 'removed',
      pageId: page?.id || null,
      name: String(page?.name || 'Untitled'),
      oldName: null,
      renamed: false,
      seoChanged: false,
      reordered: false,
      sections: []
    });
  }

  // Page order: compare the sequence of MATCHED pages only (adds/removes are
  // reported separately and must not read as a reorder).
  const oldOrder = oldPages.filter((p) => [...pairs.values()].includes(p)).map((p) => p.id);
  const newOrder = newPages.filter((p) => pairs.has(p)).map((p) => pairs.get(p).id);
  const pagesReordered = oldOrder.join('\n') !== newOrder.join('\n');

  // Shared sections.
  const shared = { navigation: null, footer: null };
  for (const category of SHARED_SECTION_CATEGORIES) {
    const oldShared = oldState?.sharedSections?.[category] || null;
    const newShared = newState?.sharedSections?.[category] || null;
    const label = sharedSectionLabel(category);
    if (!oldShared && !newShared) continue;
    if (!oldShared) {
      shared[category] = { kind: 'added', label, fields: [] };
    } else if (!newShared) {
      shared[category] = { kind: 'removed', label, fields: [] };
    } else {
      const { changed, modernizedOnly, fields } = compareSectionPair(oldShared, newShared);
      if (changed) shared[category] = { kind: 'edited', label, fields };
      else if (modernizedOnly) shared[category] = { kind: 'modernized', label, fields: [] };
    }
  }

  const settings = diffSettings(oldState, newState);

  // modernizedOnly: differences exist (identical is false) but every observed
  // one is volatile-stamp churn.
  const anyReal =
    settings.length > 0 ||
    pagesReordered ||
    pages.some((p) => p.kind !== 'unchanged') ||
    Object.values(shared).some((change) => change && change.kind !== 'modernized');
  const anyModernized =
    pages.some((p) => p.sections.some((s) => s.kind === 'modernized')) ||
    Object.values(shared).some((change) => change?.kind === 'modernized');

  // The identity fast-path hashes the WHOLE canonical state; the observers
  // above deliberately ignore per-section internals (originalHtml, identity
  // fields). A change only THEY can see must still say something — an empty
  // summary under a non-identical verdict reads as "no changes", which is a
  // lie the folding UI would repeat.
  const internalOnly = !anyReal && !anyModernized;

  return {
    identical: false,
    modernizedOnly: !anyReal && anyModernized,
    internalOnly,
    pagesReordered,
    pages,
    shared,
    settings
  };
}

// ── summaries ───────────────────────────────────────────────

const SECTION_VERBS = { edited: 'edited', added: 'added', removed: 'removed', moved: 'moved' };

function pageSummary(page) {
  if (page.kind === 'added') return `${page.name} page added`;
  if (page.kind === 'removed') return `${page.name} page removed`;
  if (page.kind === 'not-loaded')
    return `${page.name}: contents not loaded — can't compare sections`;
  if (page.kind === 'renamed') return `${page.oldName} renamed to ${page.name}`;
  if (page.kind === 'unchanged') return null;

  const parts = [];
  const meaningful = page.sections.filter((s) => s.kind !== 'modernized');
  for (const section of meaningful) {
    parts.push(`${section.label} ${SECTION_VERBS[section.kind] || 'changed'}`);
  }
  if (page.seoChanged) parts.push('SEO updated');
  if (page.renamed) parts.push(`renamed from ${page.oldName}`);
  if (parts.length === 0 && page.reordered) return `${page.name}: sections reordered`;
  if (parts.length === 0) return null;
  if (parts.length === 1) return `${page.name}: ${parts[0]}`;
  if (parts.length === 2) return `${page.name}: ${parts[0]} and ${parts[1]}`;
  return `${page.name}: ${parts.length} changes`;
}

/**
 * Ordered human summary: pages → shared → settings, capped at `limit` with an
 * explicit remainder count (never a silent truncation).
 * @returns {{items: string[], more: number}}
 */
export function summarizeVersionDiff(diff, { limit = 6 } = {}) {
  if (!diff || diff.identical) return { items: [], more: 0 };
  if (diff.modernizedOnly) {
    return { items: ['No content changes (format updated by a newer editor)'], more: 0 };
  }
  if (diff.internalOnly) {
    return { items: ['Internal data updated (no visible content changes)'], more: 0 };
  }

  const identityFieldOnly = (change) =>
    change?.fields?.length > 0 && change.fields.every((field) => field === 'editableIdRepairs');
  const identityOnly =
    diff.pages.every(
      (page) =>
        ['unchanged', 'edited'].includes(page.kind) &&
        page.sections.every(
          (section) =>
            ['unchanged', 'modernized'].includes(section.kind) ||
            (section.kind === 'edited' && identityFieldOnly(section))
        )
    ) &&
    !diff.settings.length &&
    !diff.pagesReordered &&
    Object.values(diff.shared || {}).every(
      (change) =>
        !change ||
        change.kind === 'modernized' ||
        (change.kind === 'edited' && identityFieldOnly(change))
    );
  if (identityOnly) return { items: ['Internal element identity updated'], more: 0 };
  const items = [];
  for (const page of diff.pages) {
    const line = pageSummary(page);
    if (line) items.push(line);
  }
  for (const category of SHARED_SECTION_CATEGORIES) {
    const change = diff.shared?.[category];
    if (!change || change.kind === 'modernized') continue;
    items.push(`${change.label} changed — affects all pages`);
  }
  if (diff.pagesReordered) items.push('Pages reordered');
  for (const setting of diff.settings) items.push(setting.label);

  if (items.length <= limit) return { items, more: 0 };
  return { items: items.slice(0, limit), more: items.length - limit };
}

/** One-line form for compact rows. Empty string when nothing changed. */
export function versionSummaryLine(diff, { limit = 3 } = {}) {
  const { items, more } = summarizeVersionDiff(diff, { limit });
  if (!items.length) return '';
  return more > 0 ? `${items.join(' · ')} · +${more} more` : items.join(' · ');
}
